import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { CardListToolbar } from './ui/CardListToolbar';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { AlertMessage } from './ui/AlertMessage';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { Modal } from './ui/Modal';
import { EmptyState } from './ui/EmptyState';
import { SkeletonRow } from './ui/Skeleton';
import { formControlClass, formLabelClass } from './ui/fieldStyles';

import { messageFromFetchResponse } from '../api/parseFetchError';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

function SubCategories() {
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageMessage, setPageMessage] = useState('');
  const [pageError, setPageError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingItem, setEditingItem] = useState(null);
  const [formCategoryId, setFormCategoryId] = useState('');
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const headers = useMemo(() => {
    const token = localStorage.getItem('token');
    const h = { 'Content-Type': 'application/json' };
    if (token) {
      h.Authorization = `Bearer ${token}`;
    }
    return h;
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/insights?type=CATEGORY`, { headers });
      if (!res.ok) {
        throw new Error('Unable to load categories');
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setCategories(list);
      if (list.length > 0 && !selectedCategoryId) {
        setSelectedCategoryId(String(list[0].id));
      }
    } catch (err) {
      setPageError(err?.message || 'Unable to load categories');
    }
  };

  const fetchSubCategories = async (parentId) => {
    if (!parentId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setPageError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/insights?type=SUB_CATEGORY&parentId=${encodeURIComponent(parentId)}`,
        { headers }
      );
      if (!res.ok) {
        throw new Error('Unable to load sub categories');
      }
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      setPageError(err?.message || 'Unable to load sub categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSubCategories(selectedCategoryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryId]);

  useEffect(() => {
    if (!pageMessage) return undefined;
    const timer = setTimeout(() => setPageMessage(''), 3500);
    return () => clearTimeout(timer);
  }, [pageMessage]);

  useEffect(() => {
    if (!pageError) return undefined;
    const timer = setTimeout(() => setPageError(''), 5000);
    return () => clearTimeout(timer);
  }, [pageError]);

  useEffect(() => {
    if (!formError) return undefined;
    const timer = setTimeout(() => setFormError(''), 5000);
    return () => clearTimeout(timer);
  }, [formError]);

  const filteredItems = useMemo(() => {
    const key = search.trim().toLowerCase();
    if (!key) return items;
    return items.filter((item) => String(item.name || '').toLowerCase().includes(key));
  }, [items, search]);

  const openCreateForm = () => {
    setFormMode('create');
    setEditingItem(null);
    setFormCategoryId(selectedCategoryId || (categories[0] ? String(categories[0].id) : ''));
    setName('');
    setNameError('');
    setCategoryError('');
    setFormError('');
    setShowForm(true);
  };

  const openEditForm = (item) => {
    setFormMode('edit');
    setEditingItem(item);
    setFormCategoryId(String(item.parentId || selectedCategoryId || ''));
    setName(String(item.name || ''));
    setNameError('');
    setCategoryError('');
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormMode('create');
    setEditingItem(null);
    setFormCategoryId('');
    setName('');
    setNameError('');
    setCategoryError('');
    setFormError('');
    setSaving(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPageMessage('');
    setPageError('');
    setFormError('');
    setNameError('');
    setCategoryError('');

    const trimmedName = name.trim();
    if (!formCategoryId) {
      setCategoryError('Select a category');
      return;
    }
    if (!trimmedName) {
      setNameError('Please enter name');
      return;
    }

    setSaving(true);
    try {
      const isCreate = formMode === 'create';
      const response = await fetch(
        isCreate ? `${API_BASE_URL}/api/insights` : `${API_BASE_URL}/api/insights/${editingItem.id}`,
        {
          method: isCreate ? 'POST' : 'PUT',
          headers,
          body: JSON.stringify({
            type: 'SUB_CATEGORY',
            name: trimmedName,
            parentId: formCategoryId,
          }),
        }
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error || errData?.message || 'Unable to save record');
      }
      const saved = await response.json();
      // If saved row's parent matches the active filter, reflect change locally; otherwise refetch.
      if (String(saved.parentId || '') === String(selectedCategoryId || '')) {
        if (isCreate) {
          setItems((prev) => [saved, ...prev]);
        } else {
          setItems((prev) => prev.map((item) => (item.id === saved.id ? saved : item)));
        }
      } else {
        // moved to a different category — drop from current list if it was there
        setItems((prev) => prev.filter((item) => item.id !== saved.id));
      }
      setPageMessage(isCreate ? 'Sub category created successfully' : 'Sub category updated successfully');
      closeForm();
    } catch (err) {
      setFormError(err?.message || 'Unable to save record');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteItem) return;
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/insights/${confirmDeleteItem.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) {
        throw new Error(await messageFromFetchResponse(response, 'Unable to delete record'));
      }
      setItems((prev) => prev.filter((item) => item.id !== confirmDeleteItem.id));
      setConfirmDeleteItem(null);
      setPageMessage('Sub category deleted successfully');
    } catch (err) {
      setConfirmDeleteItem(null);
      setPageError(err?.message || 'Unable to delete record');
    } finally {
      setDeleteLoading(false);
    }
  };

  const hasCategories = categories.length > 0;

  return (
    <div>
      <Card>
        <CardHeader variant="gradient">
          <CardListToolbar
            tone="light"
            title="Sub Categories"
            actions={
              <>
                <div className="w-full sm:w-56">
                  <label htmlFor="sc-category-filter" className="sr-only">Category</label>
                  <select
                    id="sc-category-filter"
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    className={formControlClass()}
                    disabled={!hasCategories}
                  >
                    {!hasCategories && <option value="">No categories yet</option>}
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div className="w-full sm:w-64">
                  <Input
                    type="text"
                    placeholder="Search sub categories..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    leftIcon={
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    }
                  />
                </div>
                <Button
                  type="button"
                  variant="primary"
                  onClick={openCreateForm}
                  disabled={!hasCategories}
                  leftIcon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  }
                >
                  Add Sub Category
                </Button>
              </>
            }
          />
        </CardHeader>
        <CardBody>
          <AlertMessage message={pageMessage} type="success" onClose={() => setPageMessage('')} className="mb-4" />
          <AlertMessage message={pageError} type="error" onClose={() => setPageError('')} className="mb-4" />

          {!hasCategories ? (
            <EmptyState
              title="No categories yet"
              description="Create a category first; sub categories are scoped to a parent category."
            />
          ) : !loading && filteredItems.length === 0 ? (
            <EmptyState
              title={search ? 'No matches found' : 'No sub categories yet'}
              description={search
                ? 'Try a different search term.'
                : 'Create your first sub category under this category.'}
              action={!search ? (
                <Button type="button" variant="primary" onClick={openCreateForm}>
                  Add Sub Category
                </Button>
              ) : null}
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th className="w-14">#</th>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th className="text-right pr-6">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} columns={6} />)
                    : filteredItems.map((item, index) => (
                        <tr key={item.id}>
                          <td className="text-slate-400 tabular-nums">{index + 1}</td>
                          <td className="font-medium text-slate-800">{item.name || '-'}</td>
                          <td className="text-slate-500">{item.parentName || '—'}</td>
                          <td className="text-slate-500">{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}</td>
                          <td className="text-slate-500">{item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : '—'}</td>
                          <td className="pr-6">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => openEditForm(item)}
                                className="icon-action icon-action-primary"
                                title="Edit"
                                aria-label={`Edit ${item.name}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setPageError('');
                                  setPageMessage('');
                                  setConfirmDeleteItem(item);
                                }}
                                className="icon-action icon-action-danger"
                                title="Delete"
                                aria-label={`Delete ${item.name}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={showForm}
        onClose={closeForm}
        size="lg"
        title={formMode === 'create' ? 'Add Sub Category' : 'Edit Sub Category'}
        description={formMode === 'edit' ? 'Update this sub category.' : undefined}
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        }
        footer={
          <>
            <Button type="button" variant="secondary" onClick={closeForm} disabled={saving}>Cancel</Button>
            <Button type="submit" form="sub-category-form" variant="primary" loading={saving} disabled={saving}>
              {formMode === 'create' ? 'Save' : 'Update'}
            </Button>
          </>
        }
      >
        <form id="sub-category-form" onSubmit={handleSubmit} className="py-6 space-y-5">
          <AlertMessage message={formError} type="error" onClose={() => setFormError('')} />
          <div>
            <label className={formLabelClass} htmlFor="sub-category-parent">Category *</label>
            <select
              id="sub-category-parent"
              value={formCategoryId}
              onChange={(e) => {
                setFormCategoryId(e.target.value);
                if (categoryError) setCategoryError('');
              }}
              className={formControlClass({ error: Boolean(categoryError) })}
            >
              <option value="">Select category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            {categoryError && <p className="mt-1.5 text-sm text-rose-600">{categoryError}</p>}
          </div>
          <Input
            label="Sub Category Name *"
            placeholder="Sub category name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError('');
            }}
            error={nameError}
            required
            autoFocus
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmDeleteItem)}
        title="Delete Sub Category"
        message={confirmDeleteItem ? `Do you want to delete "${confirmDeleteItem.name}"?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteItem(null)}
        loading={deleteLoading}
        variant="danger"
      />
    </div>
  );
}

export default SubCategories;
